import { textBlock, textInline } from '@/uiPrimitives/typography/text';
import { getBarChartProps } from './helpers/getBarChartProps';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { colors } from '@/uiPrimitives/color/colors.css';
import { formatNumber } from '@dop/shared/helpers/string';
import {
	cellWithBlockEndBorderCSS,
	cellWithInlinePaddingCSS,
	cellWithPositiveBackgroundCSS,
	tableCSS,
	tableCellCSS,
	tableFontStyleCSS,
	tableHeaderCSS,
} from '../cbsGraph.css';
import { position } from '@/uiPrimitives/layout/position';
import { block } from '@/uiPrimitives/utility/display.css';

export const BarChartDataTable = ({
	unit,
	decimals,
	comparingDimension,
	comparingDimensionInfo,
	valuesByComparingDimension,
	axis: { yAxisEntries, yAxisDimensionInfo, yAxisDimension },
}: ReturnType<typeof getBarChartProps>) => {
	const hasRowHeadings = comparingDimension !== yAxisDimension;

	const columnCount = yAxisEntries.length;

	return (
		<table className={classes(tableCSS, box({ paddingBlockStart: '0 | p' }))}>
			<thead
				className={classes(
					position({ position: 'sticky', insetBlockStart: '0' }),
				)}
			>
				<tr>
					{hasRowHeadings && (
						<td
							style={{
								backgroundColor: colors.shape.negative.plain,
							}}
						/>
					)}
					<th
						scope="column"
						colSpan={columnCount}
						className={classes(
							box({ backgroundColor: 'negative', paddingBlockEnd: '0 | p' }),
							tableCellCSS,
						)}
					>
						<span
							className={classes(
								textBlock({ fontWeight: 'bold' }),
								tableFontStyleCSS,
							)}
						>
							{yAxisDimensionInfo.title}
							<span
								className={classes(
									textInline({ color: 'positiveMuted', fontWeight: 'normal' }),
								)}
							>
								{` (${unit})`}
							</span>
						</span>
					</th>
				</tr>
				<tr>
					{hasRowHeadings && (
						<th
							scope="column"
							className={classes(
								position({ position: 'relative' }),
								tableHeaderCSS,
							)}
						>
							<svg
								viewBox="0 0 120 70"
								className={classes(
									position({
										position: 'absolute',
										insetInlineEnd: '0',
										insetBlockEnd: '0',
									}),
									block,
								)}
								style={{
									inlineSize: '180px',
									blockSize: '105px',
								}}
							>
								<path
									d="M120,70 l-70,-70 L0,0 v70"
									fill={colors.shape.negative.plain}
								/>
								<path
									d="M120,70 l-70,-70"
									stroke={colors.shape.positive.muted}
									strokeWidth="1"
								/>
							</svg>
							<span
								className={classes(
									textBlock({ fontWeight: 'bold' }),
									tableFontStyleCSS,
									position({ position: 'relative' }),
									box({
										paddingBlockStart: '0 | p',
										paddingBlockEnd: '4 | betweenListItems',
										paddingInlineEnd: '6 | betweenH4Sections',
									}),
								)}
							>
								{comparingDimensionInfo.title}
							</span>
						</th>
					)}
					{yAxisEntries.map(({ title }, index) => (
						<th
							key={index}
							scope="column"
							className={classes(tableHeaderCSS)}
							style={{ minInlineSize: 'min(30cqi, 8rem)' }}
						>
							<span
								className={classes(
									textBlock({
										fontWeight: 'bold',
										textAlign: 'center',
									}),
									tableFontStyleCSS,
								)}
							>
								{title}
								<i
									className={classes(
										box({
											borderBlockEndStyle: 'solid',
											borderColor: 'positiveBlue',
											borderWidth: '3px',
										}),
										position({ position: 'relative' }),
									)}
									style={{ insetBlockEnd: '-5px' }}
								/>
							</span>
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{valuesByComparingDimension.map(
					({ dataList, title }, dimensionIndex) => (
						<tr key={dimensionIndex}>
							{hasRowHeadings && (
								<th
									className={classes(
										cellWithBlockEndBorderCSS,
										cellWithPositiveBackgroundCSS,
										cellWithInlinePaddingCSS,
									)}
									style={{ minInlineSize: 'min(30cqi, 10rem)' }}
								>
									<span
										className={classes(
											textBlock({
												fontWeight: 'bold',
												textAlign: 'end',
											}),
											tableFontStyleCSS,
										)}
									>
										{title}
									</span>
								</th>
							)}
							{dataList.map(({ value }, index) => {
								return (
									<td
										key={`${dimensionIndex}_${index}`}
										className={classes(
											cellWithBlockEndBorderCSS,
											cellWithInlinePaddingCSS,
										)}
									>
										<span
											className={classes(
												textBlock({ textAlign: 'end' }),
												tableFontStyleCSS,
											)}
										>
											{formatNumber({ decimals, number: value })}
										</span>
									</td>
								);
							})}
						</tr>
					),
				)}
			</tbody>
		</table>
	);
};
